import React, { FormEvent, useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useIntl } from 'react-intl'
import useGetUserData from '../../../../../Utils/useGetUserData'
import { announceSession } from '../../../../../hooks/queries/useAuth'
import { useChallengeEnrollment, useConfirmChallengeEnrollment } from '../../../../../hooks/queries/useTwoFactor'
import { isRateLimited, readTwoFactorError } from '../../../../../lib/twoFactor/errors'
import { useTwoFactorChallengeStore } from '../../../../../store/twoFactorChallengeStore'
import { CodeField } from './CodeField'
import { QrEnrollment } from './QrEnrollment'
import { RecoveryCodes } from './RecoveryCodes'
import { clockFormat, useCountdown } from './useCountdown'

const LOGIN_PATH = '/auth/login'

/**
 * Setup screen (public) — a user who MUST enrol cannot sign in without it.
 *
 * Fetches a secret and QR for the challenge, takes the first code, and — on the
 * confirm — is both enrolled and signed in (cookies are set), so the recovery
 * codes appear here, once, with an explicit "I saved them" before the app opens.
 */
export const TwoFactorSetup: React.FC = () => {
  const intl = useIntl()
  const t = (id: string, values?: Record<string, string>) => intl.formatMessage({ id: `TWO_FACTOR.${id}` }, values)
  const navigate = useNavigate()
  const getUserData = useGetUserData()

  const challenge = useTwoFactorChallengeStore((s) => s.challenge)
  const endChallenge = useTwoFactorChallengeStore((s) => s.end)
  const active = challenge?.kind === 'setup' ? challenge : null
  const token = active?.token ?? null

  const enroll = useChallengeEnrollment(token)
  const confirm = useConfirmChallengeEnrollment()

  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null)
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | undefined>()
  const leaving = useRef(false)

  const enrollment = enroll.data ?? null
  // The countdown stops once the user is signed in: the codes screen has no deadline.
  const remaining = useCountdown(recoveryCodes ? null : active?.expiresAt ?? null)

  const backToSignIn = (notice?: 'expired') => {
    leaving.current = true
    endChallenge(notice)
    navigate(LOGIN_PATH, { replace: true })
  }

  useEffect(() => {
    if (leaving.current || active || recoveryCodes) return
    navigate(LOGIN_PATH, { replace: true })
  }, [active, recoveryCodes, navigate])

  useEffect(() => {
    if (remaining === 0 && !leaving.current) backToSignIn('expired')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remaining])

  const enrollFailure = enroll.error
  useEffect(() => {
    if (enrollFailure && readTwoFactorError(enrollFailure).code === 'challenge_invalid' && !leaving.current) {
      backToSignIn('expired')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enrollFailure])

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!token || confirm.isPending) return
    const trimmed = code.trim()
    if (!trimmed) {
      setError(t('setup.errors.required'))
      return
    }
    setError(undefined)
    confirm.mutate(
      { challenge_token: token, code: trimmed },
      {
        onSuccess: (data) => {
          setCode('')
          setRecoveryCodes(data.recovery_codes)
        },
        onError: (err) => {
          const failure = readTwoFactorError(err)
          if (failure.code === 'challenge_invalid') {
            backToSignIn('expired')
            return
          }
          if (failure.code === 'two_factor_no_pending_enrollment') {
            // The setup was interrupted: mint a fresh secret.
            setCode('')
            void enroll.refetch()
            return
          }
          setCode('')
          setError(
            failure.code === 'invalid_code'
              ? t('setup.errors.invalidCode')
              : isRateLimited(failure)
                ? t('errors.rateLimited')
                : t('errors.generic'),
          )
        },
      },
    )
  }

  const enterApp = () => {
    leaving.current = true
    setRecoveryCodes(null)
    announceSession()
    endChallenge()
    // The session cookie is set: loading the profile flips the router into the app.
    getUserData()
  }

  if (!active && !recoveryCodes) return null

  if (recoveryCodes) {
    return (
      <div className='card'>
        <div className='px-md-5 py-5 card-body'>
          <div className='d-flex flex-column align-items-center mb-6 text-center'>
            <h1>{t('setup.codes.title')}</h1>
            <span className='opacity-75'>{t('setup.codes.subtitle')}</span>
          </div>
          <RecoveryCodes codes={recoveryCodes} onAcknowledge={enterApp} acknowledgeLabel={t('setup.codes.continue')} />
        </div>
      </div>
    )
  }

  const loadFailed = enroll.isError && !enrollment

  return (
    <div className='card'>
      <div className='px-md-5 py-5 card-body'>
        <div className='d-flex flex-column align-items-center mb-6 text-center'>
          <h1>{t('setup.title')}</h1>
          <span className='opacity-75'>{t('setup.subtitle')}</span>
        </div>
        {loadFailed ? (
          <div className='d-flex flex-column gap-3'>
            <div className='alert alert-danger mb-0' role='alert'>
              {t('setup.errors.loadFailed')}
            </div>
            <button type='button' className='btn btn-light-primary' onClick={() => void enroll.refetch()}>
              {t('setup.retry')}
            </button>
          </div>
        ) : !enrollment ? (
          <div className='text-center py-10' role='status' aria-label={t('setup.loading')}>
            <span className='spinner-border text-primary' />
          </div>
        ) : (
          <form onSubmit={submit} noValidate id='two_factor_setup_form'>
            <QrEnrollment enrollment={enrollment} />
            <CodeField
              value={code}
              onChange={setCode}
              error={error}
              disabled={confirm.isPending}
              acceptsRecoveryCode={false}
              label={t('setup.codeLabel')}
              autoFocus
            />
            <button type='submit' className='btn-block w-100 btn btn-lg btn-primary' disabled={confirm.isPending}>
              {confirm.isPending ? t('setup.pleaseWait') : t('setup.confirm')}
            </button>
          </form>
        )}
        {remaining !== null && (
          <p className='mt-4 mb-0 text-muted fs-7 text-center' role='timer' aria-live='off'>
            {t('setup.expiresIn', { time: clockFormat(remaining, (n) => intl.formatNumber(n)) })}
          </p>
        )}
        <div className='mt-6 text-center'>
          <Link to={LOGIN_PATH} onClick={() => backToSignIn()}>
            {t('backToSignIn')}
          </Link>
        </div>
      </div>
    </div>
  )
}
